import { useCallback, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { getMe, getTokenLocalStorage } from './actions/auth';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import { parseJwt } from './helpers/jwt';
import { AuthActionTypeEnum } from './interfaces/Reducer.interface';
import { IAuthState } from './reducers/auth';

const App = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticating, isAuthenticated, authUser } = useSelector(
    (state: RootStateOrAny) => state.auth as IAuthState
  );

  const verifyToken = useCallback(async () => {
    try {
      const { accessToken } = getTokenLocalStorage();
      if (!accessToken) {
        dispatch({
          type: AuthActionTypeEnum.SET_AUTHENTICATING,
          payload: {
            isAuthenticating: false,
          },
        });
        navigate('/login');
        return;
      }

      if (!!accessToken) {
        const { sub, role } = parseJwt(accessToken);
        unstable_batchedUpdates(() => {
          dispatch({
            type: AuthActionTypeEnum.LOGIN_SUCCESS,
            payload: {
              authUser: {
                id: sub,
                role,
              },
            },
          });
        });

        // Fetch current user data
        dispatch(getMe());
      }
      dispatch({
        type: AuthActionTypeEnum.SET_AUTHENTICATING,
        payload: {
          isAuthenticating: false,
        },
      });
    } catch (e) {
      console.error('App verifyToken error:', e);
      dispatch({
        type: AuthActionTypeEnum.LOGOUT_REQUEST,
      });
      navigate('/login');
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  if (isAuthenticating) {
    return <LoadingSpinner />;
  }

  return <Outlet />;
};
export default App;
